import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";

import Spinner from "@components/Spinner";
import { fetchInvoiceDifferences } from "@service/arInvoiceApiService";

interface DiffField {
  saved: number | string | null;
  recalculated: number | string | null;
  delta: number | null;
  changed?: boolean;
}

interface InvoiceDiffRow {
  "Detail ID": number;
  "B&B ID": string | number | null;
  Payee: string | null;
  Memo: string | null;
  "Plant ID": number | null;
  "Facility ID": number | null;
  Facility: string | null;
  "Invoice Code": string | null;
  "Invoice #": string | null;
  Amount: number | null;
  "Amount (Recalc)": number | null;
  "Amount Δ": number | null;
  "Has Difference": boolean;
  "Pay Period": string | null;
  diff: Record<string, DiffField>;
}

// Order/format of fields in the diff modal.
const HOURS_FIELDS = ["Hours", "Reg Hours", "OT Hours"];
const TEXT_FIELDS = ["Rate Type"];
const DIFF_FIELD_ORDER = [
  "Amount",
  "Gross",
  "Hours",
  "Reg Hours",
  "OT Hours",
  "Bill Rate",
  "Overtime Rate",
  "Other Additions",
  "Holiday",
  "PTO",
  "Rate Type",
];

function getEndDateStr(startStr: string): string {
  const start = new Date(startStr + "T00:00:00");
  start.setDate(start.getDate() + 6);
  const year = start.getFullYear();
  const month = String(start.getMonth() + 1).padStart(2, "0");
  const day = String(start.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const currencyFormatter = (params: { value: any }) => {
  const val = parseFloat(params.value);
  if (params.value == null || isNaN(val)) return "";
  return `$${val.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const decimalFormatter = (params: { value: any }) => {
  const val = parseFloat(params.value);
  if (params.value == null || isNaN(val)) return "";
  return val.toFixed(2);
};

const fmtCurrency = (v: number | string | null) => {
  if (v == null || v === "") return "—";
  const n = typeof v === "number" ? v : parseFloat(String(v));
  if (isNaN(n)) return String(v);
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const safeSizeColumnsToFit = (params: { api?: any }) => {
  const api = params.api;
  if (!api || api.isDestroyed?.()) return;
  try {
    api.sizeColumnsToFit();
  } catch {
    /* grid not ready / no longer mounted — ignore */
  }
};

const DifferenceCellRenderer = (props: { value: boolean }) => (
  <div
    className={`tw-h-full tw-flex tw-items-center tw-justify-center tw-px-2 ${
      props.value
        ? "tw-bg-red-100 tw-text-red-800"
        : "tw-bg-green-100 tw-text-green-800"
    }`}
  >
    {props.value ? "Changed" : "Same"}
  </div>
);

const deltaCellRenderer = (params: { value: any }) => {
  const val = parseFloat(params.value);
  if (params.value == null || isNaN(val) || val === 0) {
    return <span className="tw-text-gray-400">—</span>;
  }
  const text = `${val > 0 ? "+" : ""}$${val.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  return <span className="tw-text-red-700 tw-font-medium">{text}</span>;
};

interface InvoiceDifferencesTabProps {
  periodStart: string;
  refreshSignal?: number;
}

export function InvoiceDifferencesTab({
  periodStart,
  refreshSignal,
}: InvoiceDifferencesTabProps) {
  const [rows, setRows] = useState<InvoiceDiffRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [onlyDiff, setOnlyDiff] = useState(true);
  const [detailRow, setDetailRow] = useState<InvoiceDiffRow | null>(null);

  const fetchData = useCallback(async (startStr: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchInvoiceDifferences({
        start_date: startStr,
        end_date: getEndDateStr(startStr),
      });
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to fetch differences");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(periodStart);
  }, [periodStart, refreshSignal, fetchData]);

  const visibleRows = useMemo(
    () => (onlyDiff ? rows.filter((r) => r["Has Difference"]) : rows),
    [rows, onlyDiff]
  );

  // Group by plant + facility (same look as Invoice Details).
  const groups = useMemo(() => {
    const map: Record<
      string,
      { plantId: number; facilityId: number; label: string; rows: InvoiceDiffRow[] }
    > = {};
    visibleRows.forEach((row) => {
      const plantName = row.Memo ?? "Unknown";
      const facilityName = row.Facility;
      const invoiceCode = row["Invoice Code"];
      const facilityLabel = facilityName
        ? `${invoiceCode ? ` — ${facilityName} ${invoiceCode}` : ""}`
        : "";
      const key = `${row["Plant ID"] ?? "x"}__${row["Facility ID"] ?? "x"}`;
      if (!map[key])
        map[key] = {
          plantId: row["Plant ID"] ?? Infinity,
          facilityId: row["Facility ID"] ?? Infinity,
          label: `${plantName} ${facilityLabel}`,
          rows: [],
        };
      map[key].rows.push(row);
    });
    return Object.values(map)
      .sort((a, b) => a.plantId - b.plantId || b.facilityId - a.facilityId)
      .map((g) => [g.label, g.rows] as [string, InvoiceDiffRow[]]);
  }, [visibleRows]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      { field: "B&B ID", headerName: "B&B ID", width: 150 },
      { field: "Payee", headerName: "Payee", width: 280 },
      { field: "Invoice #", headerName: "Invoice #", width: 130 },
      {
        field: "Amount",
        headerName: "Amount (Saved)",
        width: 150,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Amount (Recalc)",
        headerName: "Amount (Recalc)",
        width: 150,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Amount Δ",
        headerName: "Δ Amount",
        width: 130,
        cellRenderer: deltaCellRenderer,
      },
      {
        field: "Has Difference",
        headerName: "Difference",
        width: 120,
        pinned: "right",
        cellRenderer: DifferenceCellRenderer,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, filter: true, minWidth: 90 }),
    []
  );

  const handleRowDoubleClicked = (event: any) => {
    if (event?.data) setDetailRow(event.data as InvoiceDiffRow);
  };

  const formatFieldValue = (field: string, value: number | string | null) => {
    if (value == null || value === "") return "—";
    if (TEXT_FIELDS.includes(field)) return String(value);
    if (HOURS_FIELDS.includes(field))
      return decimalFormatter({ value });
    return fmtCurrency(value);
  };

  return (
    <div className="tw-w-full">
      {error && (
        <div className="tw-text-center tw-py-4 tw-text-red-600 tw-flex tw-justify-center">
          <div>{error}</div>
        </div>
      )}

      <div className="tw-flex tw-items-center tw-gap-2 tw-px-1 tw-py-2">
        <label className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-gray-700">
          <input
            type="checkbox"
            checked={onlyDiff}
            onChange={(e) => setOnlyDiff(e.target.checked)}
          />
          Only show differences
        </label>
        <span className="tw-text-xs tw-text-gray-500">
          {rows.filter((r) => r["Has Difference"]).length} of {rows.length} with
          differences
        </span>
      </div>

      {isLoading && (
        <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-min-w-[580px] tw-min-h-[55vh]">
          <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center tw-w-full" />
          Loading differences...
        </div>
      )}

      {!isLoading && visibleRows.length === 0 && (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-text-gray-400">
          {rows.length === 0 ? (
            <SearchOffIcon sx={{ fontSize: 64 }} />
          ) : (
            <CheckCircleOutlineIcon sx={{ fontSize: 64 }} />
          )}
          <span className="tw-mt-2 tw-text-sm">
            {rows.length === 0
              ? "No invoice details found for this period"
              : "No differences for this period"}
          </span>
        </div>
      )}

      {!isLoading && visibleRows.length > 0 && (
        <div className="tw-w-full">
          <div className="tw-rounded-r-lg tw-border tw-border-gray-200">
            {groups.map(([label, groupRows]) => (
              <div className="tw-bg-white tw-p-4 tw-mb-3" key={label}>
                <h2 className="tw-rounded tw-p-1 tw-bg-[#2642c9] tw-text-white tw-mb-2">
                  {label}
                </h2>
                <div
                  className="ag-theme-balham tw-w-full"
                  style={{ height: Math.min(groupRows.length * 28 + 56, 400) }}
                >
                  <AgGridReact
                    rowData={groupRows}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    animateRows={true}
                    pagination={false}
                    onRowDoubleClicked={handleRowDoubleClicked}
                    onCellDoubleClicked={handleRowDoubleClicked}
                    onGridReady={safeSizeColumnsToFit}
                    onGridSizeChanged={safeSizeColumnsToFit}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Saved vs Recalculated detail modal */}
      <Dialog
        open={detailRow !== null}
        onClose={() => setDetailRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {detailRow?.Payee ?? "Detail"} — Saved vs Recalculated
          <IconButton onClick={() => setDetailRow(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Table size="small" sx={{ "& td, & th": { fontSize: "0.78rem" } }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: 700, backgroundColor: "#d1d5db", color: "#374151" }}
                  >
                    Field
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, backgroundColor: "#d1d5db", color: "#374151" }}
                    align="right"
                  >
                    Saved
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, backgroundColor: "#d1d5db", color: "#374151" }}
                    align="right"
                  >
                    Recalculated
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, backgroundColor: "#d1d5db", color: "#374151" }}
                    align="right"
                  >
                    Δ
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {DIFF_FIELD_ORDER.filter((f) => detailRow.diff[f]).map((field) => {
                  const d = detailRow.diff[field];
                  const isText = TEXT_FIELDS.includes(field);
                  const changed = isText
                    ? d.changed
                    : d.delta != null && Math.abs(d.delta) >= 0.005;
                  return (
                    <TableRow
                      key={field}
                      sx={changed ? { backgroundColor: "#fff4f4" } : undefined}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{field}</TableCell>
                      <TableCell align="right">
                        {formatFieldValue(field, d.saved)}
                      </TableCell>
                      <TableCell align="right">
                        {formatFieldValue(field, d.recalculated)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: changed ? "#b91c1c" : "#9ca3af" }}
                      >
                        {isText
                          ? changed
                            ? "changed"
                            : "—"
                          : d.delta && Math.abs(d.delta) >= 0.005
                          ? `${d.delta > 0 ? "+" : ""}${fmtCurrency(d.delta)}`
                          : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default InvoiceDifferencesTab;
